import { useEffect, useState, useRef } from 'react';
import { getApiUrl } from '../../utils/api';

interface EmailLog {
  id: number;
  recipient: string;
  subject: string;
  status: string;
  error_msg: string | null;
  sent_at: string;
  hour_key: string;
}

export default function Home() {
  const [time, setTime] = useState(new Date());
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const lastCheckHour = useRef(-1);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch logs on mount
  useEffect(() => {
    fetchLogs();
  }, []);

  // Auto check-and-send at the top of each hour
  useEffect(() => {
    const checker = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();
      const minutes = now.getMinutes();
      if (minutes <= 2 && currentHour !== lastCheckHour.current) {
        lastCheckHour.current = currentHour;
        triggerCheckAndSend();
      }
    }, 30000); // Check every 30 seconds
    return () => clearInterval(checker);
  }, []);

  async function fetchLogs() {
    try {
      const res = await fetch(getApiUrl('/api/logs'));
      const data = await res.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
  }

  async function triggerCheckAndSend() {
    try {
      await fetch(getApiUrl('/api/check-and-send'), { method: 'POST' });
      fetchLogs();
    } catch (err) {
      console.error('Auto check failed:', err);
    }
  }

  async function handleSendNow() {
    setSending(true);
    setMessage('');
    try {
      const res = await fetch(getApiUrl('/api/send-now'), { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setMessage(data.error === 'Already sent this hour' ? '本小时已发送过' : '邮件发送成功!');
      } else {
        setMessage('发送失败: ' + (data.error || '未知错误'));
      }
      fetchLogs();
    } catch {
      setMessage('网络错误');
    } finally {
      setSending(false);
    }
  }

  const hours = String(time.getHours()).padStart(2, '0');
  const minutes = String(time.getMinutes()).padStart(2, '0');
  const seconds = String(time.getSeconds()).padStart(2, '0');
  const dateStr = time.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

  return (
    <div className="chime-page">
      <div className="clock-section">
        <h1 className="clock-title">整点报时</h1>
        <div className="clock-display">
          <span className="clock-digit">{hours}</span>
          <span className="clock-sep">:</span>
          <span className="clock-digit">{minutes}</span>
          <span className="clock-sep">:</span>
          <span className="clock-digit">{seconds}</span>
        </div>
        <p className="clock-date">{dateStr}</p>
        <p className="clock-info">每整点自动发送报时邮件至 codergma@gmail.com</p>
        <button className="btn-send" onClick={handleSendNow} disabled={sending}>
          {sending ? '发送中...' : '立即发送报时邮件'}
        </button>
        {message && <p className={`send-msg ${message.includes('失败') || message.includes('错误') ? 'error' : 'success'}`}>{message}</p>}
      </div>

      <div className="logs-section">
        <h2>发送日志</h2>
        {logs.length === 0 ? (
          <p className="empty">暂无发送记录</p>
        ) : (
          <div className="log-list">
            {logs.map((log) => (
              <div key={log.id} className={`log-item ${log.status}`}>
                <div className="log-header">
                  <span className={`log-status ${log.status}`}>
                    {log.status === 'sent' ? '已发送' : '失败'}
                  </span>
                  <span className="log-time">{new Date(log.sent_at + 'Z').toLocaleString('zh-CN')}</span>
                </div>
                <div className="log-subject">{log.subject}</div>
                {log.error_msg && <div className="log-error">{log.error_msg}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
